/**
 * Supplements lesen und schreiben — **immer lokal zuerst**.
 *
 * Jede Schreibfunktion macht zwei Dinge in einer Transaktion: die Zeile ablegen
 * und die Änderung in die Outbox stellen. Beides zusammen oder gar nicht — sonst
 * gäbe es Zustände, in denen eine Änderung sichtbar ist, aber nie beim Server
 * ankommt. Das wäre stiller Datenverlust, und der ist das einzige wirklich
 * schlimme Verhalten.
 */

import { newId } from '@kraftwerk/shared';
import type { MealSlot, SupplementForm } from '@kraftwerk/shared';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalSupplement } from './local.js';

export interface SupplementDraft {
  name: string;
  dose?: number | null;
  unit?: string | null;
  scheduleTimes?: string[];
  /** Wochentage 1–7; leer = täglich. */
  weekdays?: number[];
  mealSlot?: MealSlot | null;
  /** Gehalt je Einheit von der Packung, etwa „1 Kapsel = 1000 IE D3“. */
  note?: string | null;
  sourceNote?: string | null;
  form?: SupplementForm | null;
  barcode?: string | null;
}

function emptySupplement(id: string, now: string): LocalSupplement {
  return {
    id,
    updatedAt: now,
    deletedAt: null,
    name: '',
    dose: null,
    unit: null,
    scheduleTimes: [],
    weekdays: [],
    months: [],
    mealSlot: null,
    note: null,
    sourceNote: null,
    active: true,
    sortOrder: 0,
  };
}

export async function listSupplements(
  database: KraftwerkDatabase = db,
): Promise<LocalSupplement[]> {
  const rows = await database.supplements.toArray();
  return rows
    .filter((row) => !row.deletedAt)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'de'));
}

export async function createSupplement(
  draft: SupplementDraft,
  database: KraftwerkDatabase = db,
): Promise<LocalSupplement> {
  const now = new Date().toISOString();
  // Die Kennung entsteht **hier**, nicht auf dem Server — sonst könnte offline
  // nichts angelegt werden, auf das andere Zeilen verweisen.
  const row: LocalSupplement = {
    ...emptySupplement(newId(), now),
    ...draft,
    dose: draft.dose ?? null,
    unit: draft.unit ?? null,
    scheduleTimes: draft.scheduleTimes ?? [],
    weekdays: draft.weekdays ?? [],
    mealSlot: draft.mealSlot ?? null,
    note: draft.note ?? null,
    sourceNote: draft.sourceNote ?? null,
    form: draft.form ?? null,
    barcode: draft.barcode ?? null,
  };

  await database.transaction('rw', [database.supplements, database.outbox], async () => {
    await database.supplements.put(row);
    await database.outbox.add({
      mutationId: newId(),
      table: 'supplements',
      kind: 'upsert',
      row,
      createdAt: now,
      attempts: 0,
    });
  });

  return row;
}

export async function updateSupplement(
  id: string,
  changes: Partial<SupplementDraft>,
  database: KraftwerkDatabase = db,
): Promise<LocalSupplement | null> {
  const now = new Date().toISOString();

  return database.transaction('rw', [database.supplements, database.outbox], async () => {
    const existing = await database.supplements.get(id);
    if (!existing || existing.deletedAt) return null;

    const row: LocalSupplement = { ...existing, ...changes, updatedAt: now };
    await database.supplements.put(row);
    await database.outbox.add({
      mutationId: newId(),
      table: 'supplements',
      kind: 'upsert',
      row,
      createdAt: now,
      attempts: 0,
    });
    return row;
  });
}

export async function deleteSupplement(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const now = new Date().toISOString();

  await database.transaction('rw', [database.supplements, database.outbox], async () => {
    const existing = await database.supplements.get(id);
    if (!existing || existing.deletedAt) return;

    // Weich löschen: ein hartes Löschen ließe sich dem anderen Gerät nicht
    // mitteilen — es würde die Zeile beim nächsten Holen einfach neu bekommen.
    const row: LocalSupplement = { ...existing, deletedAt: now, updatedAt: now };
    await database.supplements.put(row);
    await database.outbox.add({
      mutationId: newId(),
      table: 'supplements',
      kind: 'delete',
      row,
      createdAt: now,
      attempts: 0,
    });
  });
}

/** Ein Supplement mit diesem Strichcode, falls schon in der Bibliothek. */
export async function supplementByBarcode(
  code: string,
  database: KraftwerkDatabase = db,
): Promise<LocalSupplement | null> {
  const rows = await listSupplements(database);
  return rows.find((row) => row.barcode === code) ?? null;
}
